// Components==============
import indexActivity from 'actions/activity/index';
import indexFeedback from 'actions/feedback/index';
import indexPortfolio from 'actions/portfolio/index';
import indexProject from 'actions/project/index';
import indexTheme from 'actions/theme/index';
import indexTrainingSubscription from 'actions/training/subscription/index';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import Container from 'components/UI/Container/Container';
import Dropdown from 'components/UI/Dropdown/Dropdown';
import NoContent from 'components/UI/NoContent/NoContent';
import PortfolioCard from 'components/UI/PortfolioCard/PortfolioCard';
import Title from 'components/UI/Title/Title';
import { AnimatePresence } from 'framer-motion';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import React, { createContext, useEffect, useState } from 'react';
import './MyPortfolios.module.scss';
import DraggableEvidenceCard from './components/DraggableEvidenceCard/DraggableEvidenceCard';
import ExportModal from './components/ExportModal/ExportModal';
import PortfolioSettings from './components/PortfolioSettings/PortfolioSettings';
import Right from './components/Right/Right';
import {
  MyPortfolioFunctions,
  useMyPortfolioFunctions,
} from './usePortfolioFunctions';
import AddContentCard from './components/AddContentCard/AddContentCard';
// =========================

export type MyPortfolioContextType = {
  currentPortfolio?: PortfolioEntity;
  currentPortfolioId: number;
  setSelectedPortfolio: React.Dispatch<React.SetStateAction<string>>;
  portfolios?: (PortfolioEntity | undefined)[];
  evidenceEntities?: (EvidenceEvidenceEntity | undefined)[];
  exportModalOpen: boolean;
  setExportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const MyPortfolioContext = createContext(
  {} as MyPortfolioContextType & MyPortfolioFunctions
);

export default function MyPortfolios() {
  const dispatch = useReduxAction();

  useEffect(() => {
    dispatch(indexTraining());
    dispatch(indexTrainingSubscription({}));
    dispatch(indexPortfolio());
    dispatch(indexTheme());
    dispatch(indexFeedback());
    dispatch(indexProject());
    dispatch(indexActivity());
  }, []);

  const userId = useRedux((state) => state.authentication?.user?.id);

  const portfolios = useRedux((state) =>
    state.portfolio?.entities.allIds
      .map((id) => state.portfolio?.entities.byId[id])
      .filter((portfolio) => portfolio?.created_by === userId)
  );

  const [currentPortfolioId, setCurrentPortfolioId] = useState(
    portfolios?.[0]?.id || 0
  );

  const currentPortfolio = portfolios?.find(
    (p) => p?.id === currentPortfolioId
  );

  const [selectedPortfolio, setSelectedPortfolio] = useState(
    portfolios?.[0]?.name || ''
  );

  useEffect(() => {
    if (!selectedPortfolio) setSelectedPortfolio(portfolios?.[0]?.name || '');
  }, [portfolios?.length]);

  useEffect(() => {
    const match = portfolios?.find((p) => p?.name === selectedPortfolio);

    if (match) setCurrentPortfolioId(match.id);
  }, [selectedPortfolio]);

  const evidenceEntities = useRedux((state) =>
    currentPortfolio?.content?.map((id) => state.evidence?.entities.byId?.[id])
  );

  const loading = useRedux(
    (state) =>
      state.portfolio?.loading ||
      state.theme?.loading ||
      state.feedback?.loading ||
      state.project?.loading ||
      state.activity?.loading ||
      state.training?.loading ||
      state.trainingSubscription?.loading
  );

  const query = useMediaQ('min', 1024);

  const [exportModalOpen, setExportModalOpen] = useState(false);

  const contextValues = {
    portfolios,
    currentPortfolio,
    currentPortfolioId,
    setSelectedPortfolio,
    evidenceEntities,
    exportModalOpen,
    setExportModalOpen,
  };

  const functions = useMyPortfolioFunctions(contextValues);

  return (
    <Layout>
      <NoContent
        loading={!!loading}
        hasContent={portfolios ? portfolios.length > 0 : false}
        page="my-portfolios"
      >
        <MyPortfolioContext.Provider
          value={{
            ...contextValues,
            ...functions,
          }}
        >
          <Container>
            <Title
              text="general.terms.show-portfolios"
              loading={loading}
              right={query && <Right />}
            />

            {query && (
              <div styleName="top">
                <div styleName="dropdown-wrapper">
                  <Dropdown
                    selectedItem={selectedPortfolio}
                    setSelectedItem={setSelectedPortfolio}
                    initialOptions={portfolios?.map((p) => p?.name || '') || []}
                    initialText={selectedPortfolio}
                  />
                </div>
                <PortfolioSettings />
              </div>
            )}
            {query ? (
              <NoContent
                page="portfolio-content"
                marginTop="5em"
                loading={false}
                hasContent={
                  !!(evidenceEntities && evidenceEntities?.length > 0)
                }
                decreasedHeight={70}
              >
                <div styleName={`desktop-wrapper ${currentPortfolio?.layout}`}>
                  {evidenceEntities?.map(
                    (evidence) =>
                      !!evidence && (
                        <div key={evidence?.id} styleName="grid-item">
                          <DraggableEvidenceCard evidence={evidence} />
                        </div>
                      )
                  )}
                  <div styleName="grid-item">
                    <AddContentCard />
                  </div>
                </div>
              </NoContent>
            ) : (
              <div styleName="mobile-wrapper">
                {portfolios?.map((portfolio) => (
                  <PortfolioCard key={portfolio?.id} item={portfolio} />
                ))}
              </div>
            )}
          </Container>
          <AnimatePresence>
            {exportModalOpen && (
              <ExportModal portfolioId={currentPortfolioId} />
            )}
          </AnimatePresence>
        </MyPortfolioContext.Provider>
      </NoContent>
    </Layout>
  );
}
